export class ImageOutputSizeError extends Error {
  constructor() {
    super("The optimized image exceeds the allowed size.");
    this.name = "ImageOutputSizeError";
  }
}

interface OptimizeImageFileOptions {
  file: File;
  outputMimeType: "image/jpeg" | "image/png";
  maxOutputBytes: number;
  targetSizeMB?: number;
  maxDimension?: number;
}

export const optimizeImageFile = async ({
  file,
  outputMimeType,
  maxOutputBytes,
  targetSizeMB = 1,
  maxDimension = 1600,
}: OptimizeImageFileOptions): Promise<Blob> => {
  const { default: imageCompression } = await import("browser-image-compression");
  const normalizedImage = await imageCompression(file, {
    maxSizeMB: targetSizeMB,
    maxWidthOrHeight: maxDimension,
    maxIteration: 15,
    initialQuality: 0.86,
    fileType: outputMimeType,
    preserveExif: false,
    // Use bundled code. The default worker can load code from an external CDN.
    useWebWorker: false,
  });

  // Compression is best effort. Check its actual result before transport.
  if (normalizedImage.size <= 0 || normalizedImage.size > maxOutputBytes) {
    throw new ImageOutputSizeError();
  }

  return normalizedImage;
};

export const imageDataUrlToFile = ({
  dataUrl,
  fileName = "image",
}: {
  dataUrl: string;
  fileName?: string;
}): File => {
  const match = /^data:(image\/[a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/]+={0,2})$/i.exec(
    dataUrl
  );
  if (!match) throw new Error("The image data is invalid.");

  try {
    const decoded = atob(match[2]);
    const bytes = new Uint8Array(decoded.length);
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index);
    }
    return new File([bytes], fileName, { type: match[1].toLowerCase() });
  } catch {
    throw new Error("The image data is invalid.");
  }
};

export const readFileAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string" || !reader.result) {
        reject(new Error("The image could not be read."));
        return;
      }
      resolve(reader.result);
    };
    reader.onerror = () => reject(new Error("The image could not be read."));
    reader.onabort = () => reject(new Error("The image read was cancelled."));
    reader.readAsDataURL(file);
  });
